/**
 * @file Style-layer.repository.ts
 * @module StyleLayerRepository
 * @description Reaktīvā stāvokļa krātuve stilizētu slāņu definīcijām (izmanto @ngneat/elf).
 *              Pārvalda katras stilizētās slāņu grupas un atsevišķa slāņa redzamību
 *              un konfigurāciju. Patērē slāņu pārslēdzēja saskarne un Mapbox stilu
 *              ģenerators, lai noteiktu, kuri objekti tiek renderēti.
 *              Konfigurācija nāk no Kartes vizualizācijas pārvaldības.
 */

import { createStore } from "@ngneat/elf";
import { deleteAllEntities, getAllEntities, selectAllEntities, upsertEntities, withEntities } from "@ngneat/elf-entities";
import { LineStyle, PointStyle } from "../store/Layers.repository";

/** Individual styled layer within a group (tracks visibility and style rules) */
export interface StyleLayerItem {
	/** Unique ID (format: "{layerName}-{backendId}") */
	id: string;
	/** Display name */
	name: string;
	/** Whether this layer is currently visible on the map */
	visible: boolean;
	/** PostgreSQL table name for MVT source */
	table: string;
	/** Line style rules for this layer */
	lines: LineStyle[];
	/** Point style rules for this layer */
	points: PointStyle[];
	/** Whether features are clustered */
	clustered: boolean;
	/** Sort order index within parent group */
	index: number;
	/** Attribution text */
	attributions: string;
	/** Original table name (before merged tile renaming) */
	originalTable?: string;
	/** Whether any data records exist for this layer */
	records: boolean;
}

/** Layer group with visibility state and child layers */
export interface StyleLayerProp {
	/** Unique group ID (format: "{groupName}-{groupId}") */
	id: string;
	/** Display name */
	name: string;
	/** Individual layers within this group */
	layers: StyleLayerItem[];
	/** Whether the entire group is visible */
	visible: boolean;
	/** URL to the open data catalog entry */
	dataSource?: string;
}

const STORE = createStore({ name: "styleLayer" }, withEntities<StyleLayerProp>({}));

export function getStyleItems() {
	return STORE.pipe(selectAllEntities());
}

export function returnStyleItems() {
	return STORE.query(getAllEntities());
}

export const updateStyleItems = (results: StyleLayerProp[]) => {
	STORE.update(upsertEntities(results));
};

export const clearStyleItems = () => {
	STORE.update(deleteAllEntities());
};
